import { Injectable } from '@angular/core';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { AuthState } from './auth-state';

const managedKeysStorageKey = 'angular-wfe-proxy-oidc-managedKeys';

/**
 * Wrapper around `OAuthStorage` that will remove all storage entries that
 * were added via this service on logout
 */
@Injectable({
  providedIn: 'root'
})
export class AuthManagedStorageService implements OAuthStorage {
  constructor(private impl: OAuthStorage) {}

  /**
   * remove all storage entries that were added through calls to `setItem`
   */
  clear() {
    this.getManagedKeys().forEach(key => {
      this.impl.removeItem(key);
    });
    this.impl.removeItem(managedKeysStorageKey);
  }

  getItem(key: string): string | null {
    return this.impl.getItem(key);
  }

  getStateItem(key: keyof AuthState): string | null {
    return this.getItem(key);
  }

  getItems() {
    const initialItems: { [key: string]: string } = {};
    return Array.from(this.getManagedKeys()).reduce((items, key) => {
      const value = this.impl.getItem(key);
      if (value !== null) {
        items[key] = value;
      }
      return items;
    }, initialItems);
  }

  removeItem(key: string): void {
    this.impl.removeItem(key);
    this.deleteManagedKey(key);
  }

  setItem(key: string, data: string): void {
    this.impl.setItem(key, data);
    this.addManagedKey(key);
  }

  updateItem(key: string, value: string | undefined) {
    if (value) {
      this.setItem(key, value);
    } else {
      this.removeItem(key);
    }
  }

  updateStateItem(key: keyof AuthState, value: string | undefined) {
    this.updateItem(key, value);
  }

  private addManagedKey(key: string) {
    const existingKeys = this.getManagedKeys();
    if (existingKeys.includes(key)) {
      return;
    }
    this.setManagedKeys([...existingKeys, key]);
  }

  private deleteManagedKey(key: string) {
    const keys = this.getManagedKeys().filter(k => k !== key);
    this.setManagedKeys(keys);
  }

  private getManagedKeys(): string[] {
    const keysList = this.impl.getItem(managedKeysStorageKey);
    const keys = keysList ? JSON.parse(keysList) : [];
    return Array.isArray(keys) ? keys : [];
  }

  private setManagedKeys(keys: string[]) {
    this.impl.setItem(managedKeysStorageKey, JSON.stringify(keys));
  }
}
